import isEmpty from 'lodash/isEmpty';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import {
  EmailUnsubscribe,
  IAml,
  IAmlResponse,
  IDocumentResponse,
  IFinancialDetails,
  IListAmlsResponse,
  IListDocumentsResponse,
  IPersonalDetails,
  IUser,
  IUtmDetails,
  UserType
} from 'stores/networkInterfaces';
import {
  getImpersonateDetails,
  IUserDetails,
  refreshUserDetails,
  setImpersonateDetails,
  updateUserDetails
} from 'utils/authUtils';
import { runRequest, safeRunRequest } from 'utils/networkRequests';
import { isNotEmpty } from 'utils/object';

interface UserResponse {
  data: {
    attributes: IUser;
  };
}

interface IUserData {
  name?: string;
  nickname?: string;
  email?: string;
  creatorId?: string;
  userId?: string;
  investorId?: string;
  personalDetails?: IPersonalDetails;
  financialDetails?: IFinancialDetails;
  sponsorId?: string;
  verified?: boolean;
}

export interface IUploadResponse {
  key: string;
  url: string;
  document_id: string;
}

class ProfileStore {
  connectState = '';

  userId = '';
  loading = {
    profile: false
  };
  userData: IUserData = {};

  constructor() {
    makeObservable(this, {
      userId: observable,
      loading: observable,
      connectState: observable,

      addWatch: action,
      removeWatch: action,

      setConnectState: action,

      isVerified: computed,

      setImpersonateUser: action,

      updateProfile: action,
      fetchProfile: action,

      sendVerifyEmail: action,
      verifyEmail: action,
      unsubscribeEmail: action,

      verifyUserName: action,
      updateUserName: action,

      listAmls: action,
      createAml: action,
      updateAml: action,
      listAmlDocuments: action,
      createAmlDocument: action,

      uploadDocument: action,
      getResourceUrl: action
    });
  }

  setConnectState(state: string) {
    this.connectState = state;
  }

  unregisterUser() {
    return runRequest('unregisterUser', {});
  }

  async addWatch(offeringId: string) {
    const body = {
      watch: {
        type: 'Watch',
        attributes: {
          offering_id: offeringId
        }
      }
    };

    await safeRunRequest('addWatch', body);
  }

  async removeWatch(offeringId: string) {
    const body = {
      watch: {
        type: 'Watch',
        attributes: {
          offering_id: offeringId
        }
      }
    };

    await safeRunRequest('removeWatch', body);
  }

  async updateProfile(userData: {
    sponsorId?: string | null;
    initialUtm?: IUtmDetails;
    personalDetails?: IPersonalDetails;
    financialDetails?: IFinancialDetails;
  }) {
    const body = {
      user: {
        type: 'User',
        attributes: {
          ...(userData?.sponsorId !== undefined && { sponsor_id: userData?.sponsorId }),
          ...(isNotEmpty(userData?.initialUtm) && { initial_utm: userData?.initialUtm }),
          ...(userData?.personalDetails && { personal_details: userData?.personalDetails }),
          ...(userData?.financialDetails && { financial_details: userData?.financialDetails })
        }
      }
    };

    try {
      this.loading.profile = true;
      const response = await safeRunRequest('userUpdate', body);
      return this.handleUserResponse(response);
    } finally {
      runInAction(() => {
        this.loading.profile = false;
      });
    }
  }

  async fetchProfile() {
    const body = {};
    try {
      this.loading.profile = true;
      const response = await safeRunRequest('userGet', body);
      return this.handleUserResponse(response);
    } finally {
      runInAction(() => {
        this.loading.profile = false;
      });
    }
  }

  get isVerified() {
    return this.userData?.verified;
  }

  async verifyUserName(name?: string) {
    const body = name
      ? {
          name
        }
      : {};

    const response = await safeRunRequest('verifyUserName', body);
    const {
      data: { names: names }
    } = response;

    return names;
  }

  async updateUserName(name: string) {
    const body = {
      name
    };

    const response = await runRequest('updateUserName', body);
    return this.handleUserResponse(response);
  }

  setImpersonateUser(user: IUser) {
    setImpersonateDetails({
      isApproved: !!(
        user?.user_type == UserType.sponsor ||
        user?.user_type == UserType.admin ||
        user?.verified ||
        user?.approved
      ),
      isManagement: user?.user_type == UserType.sponsor || user?.user_type == UserType.admin,
      isSystem: user?.user_type == UserType.admin,
      userId: user?.id,
      groups:
        user?.user_type == UserType.sponsor && !!user?.sponsor_id
          ? [`sponsor_${user?.sponsor_id}`]
          : undefined,
      investorId: user?.investor_id,
      sponsorId: user?.sponsor_id,
      creatorId: user?.creator_id,
      investorVerified: !!(user?.verified || user?.approved),
      investorAccredited: !!user?.financial_details?.accredited,
      investorPending: !!user?.pending,
      email: user?.email,
      emailVerified: user?.verified,
      name: user?.name,
      nickname: user?.display_name,
      kycDone: !!user?.financial_details?.investment_value
    });
  }

  handleUserResponse(response: UserResponse) {
    if (isEmpty(response?.data?.attributes)) {
      throw new Error('Failed to fetch user data');
    }

    const {
      data: { attributes: user }
    } = response;

    const {
      id: userId,
      name: name,
      creator_id: creatorId,
      investor_id: investorId,
      sponsor_id: sponsorId,
      display_name: displayName,
      verified: verified,
      pending: pending,
      email: email,
      financial_details: financialDetails,
      personal_details: personalDetails
    } = user;

    const impersonateDetails = getImpersonateDetails();
    if (impersonateDetails?.email == user?.email) {
      this.setImpersonateUser(user as IUser);
    }

    const userData = {
      email,
      name,
      nickname: displayName || name,
      verified,
      creatorId,
      userId,
      investorId,
      personalDetails,
      financialDetails,
      sponsorId
    } as IUserData;

    updateUserDetails({
      email,
      name,
      nickname: displayName || name,
      accredited: financialDetails?.accredited,
      verified,
      pending,
      kycDone: !!financialDetails?.investment_value
    } as IUserDetails);

    runInAction(() => {
      this.userId = userId || '';
      this.userData = userData;
    });

    return userData;
  }

  async sendVerifyEmail() {
    const body = {};

    return await safeRunRequest('sendVerifyEmail', body);
  }

  async verifyEmail(email?: string, token?: string) {
    const body = { email, token };

    return await runRequest('verifyEmail', body).then((response) => {
      refreshUserDetails();
      return response;
    });
  }

  async unsubscribeEmail(params: EmailUnsubscribe) {
    const body = { ...params };
    return await runRequest('unsubscribeEmail', body).then((response) => {
      return response;
    });
  }

  handleAmlListResponse(response: IListAmlsResponse) {
    return {
      total: response?.total,
      data: response?.data
        ? response?.data?.map((aml) => {
            return { ...aml.attributes };
          })
        : []
    };
  }

  async listAmls() {
    return safeRunRequest('listAmls', {}).then((response) => this.handleAmlListResponse(response));
  }

  handleAmlResponse(response: IAmlResponse) {
    return response?.data?.attributes;
  }

  async createAml(aml: IAml) {
    const body = {
      aml: {
        type: 'Aml',
        attributes: aml
      }
    };

    const response = await safeRunRequest('createAml', body);
    return this.handleAmlResponse(response);
  }

  async updateAml(aml: IAml) {
    const body = {
      aml: {
        type: 'Aml',
        attributes: aml
      }
    };

    const response = await safeRunRequest('updateAml', body);
    return this.handleAmlResponse(response);
  }

  async deleteAml(amlId: string) {
    const body = {
      id: amlId
    };

    const response = await safeRunRequest('deleteAml', body);
    return this.handleAmlResponse(response);
  }

  listAmlDocuments(amlId: string) {
    const body = {
      aml_id: amlId
    };

    return safeRunRequest('listDocuments', body).then((response) =>
      this.handleDocumentListResponse(response)
    );
  }

  createAmlDocument(amlId: string, fileName: string, documentId: string) {
    const body = {
      document: {
        type: 'Document',
        attributes: {
          aml_id: amlId,
          name: fileName,
          document_id: documentId
        }
      }
    };

    return runRequest('createDocument', body).then((response) =>
      this.handleDocumentResponse(response)
    );
  }

  uploadDocument(fileName: string, fileType: string) {
    const body = {
      file_name: fileName,
      file_type: fileType
    };

    return safeRunRequest('uploadDocument', body).then(
      (response) => response?.data as IUploadResponse
    );
  }

  getResourceUrl(key: string, offeringId?: string) {
    const body = {
      key: key,
      offering_id: offeringId
    };

    return safeRunRequest('getResource', body).then((response) => response?.data);
  }

  handleDocumentListResponse(response: IListDocumentsResponse) {
    return {
      total: response?.total,
      data: response?.data
        ? response?.data?.map((document) => {
            return { ...document.attributes };
          })
        : []
    };
  }

  handleDocumentResponse(response: IDocumentResponse) {
    return response?.data?.attributes;
  }
}

export const ProfileStoreInstance = new ProfileStore();

export default ProfileStore;
